<template>
  <r-ai-page-container :current-step="5">
    <v-row class="text-start">
      <v-col cols="12">
        <div class="pb-4 text-body-1" v-if="!addMode">
          {{ textIntro }}
        </div>
        <r-ai-bubble v-if="!addMode && !isEditing">
          <ai-line title="School" :text="educationDisplayLine1"></ai-line>
          <ai-line
            :error="degreeError"
            title="Degree"
            :text="educationDisplayLine2"
          ></ai-line>
          <ai-line title="Location" :text="educationDisplayLine3"></ai-line>
          <ai-line title="GPA" :text="educationDisplayLine4"></ai-line>
        </r-ai-bubble>
        <r-ai-bubble v-if="isEditing" class="text-body-1 mt-2 mb-5">
          <div class="py-2 mb-2">Education Details</div>
          <v-text-field
            v-model="universityName"
            label="Institution Name (University or College Name)"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="universityCity"
            label="City"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="universityState"
            label="State"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="country"
            label="Country"
            outlined
            dense
          ></v-text-field>
          <v-combobox
            v-model="degreeName"
            label="Degree Type"
            :items="baseDegrees"
            outlined
            dense
          ></v-combobox>
          <v-text-field
            v-model="degreeArea"
            label="Area of Study"
            outlined
            dense
          ></v-text-field>
          <v-text-field
            v-model="minors"
            label="Minors"
            outlined
            dense
          ></v-text-field>
          <v-text-field v-model="gpa" label="GPA" outlined dense></v-text-field>
          <v-text-field
            v-model="gpaScale"
            label="GPA Scale (ie 4.0, 5.0)"
            outlined
            dense
          ></v-text-field>
          <v-switch
            class="mt-0"
            v-model="graduated"
            label="Have you graduated?"
          ></v-switch>
          <ai-month-date-select
            label="Start Date"
            :value.sync="dateStart"
          ></ai-month-date-select>
          <ai-month-date-select
            v-if="!graduated"
            label="End Date"
            :value.sync="dateEnd"
          ></ai-month-date-select>
        </r-ai-bubble>
        <div v-if="isEditing">
          <v-btn
            class="mt-2 mb-2"
            color="primary"
            outlined
            @click="save()"
            block
          >
            {{ addMode ? "Save Degree" : "Save and Continue" }}
          </v-btn>
        </div>
        <div class="pt-2" v-if="!addMode && !isEditing">
          <v-btn class="my-4" color="primary" @click="next()" block>
            Looks Good
          </v-btn>
          <v-btn class="my-4 ai-outlined-btn" outlined @click="edit()" block>
            Edit or Add Info
          </v-btn>
          <v-btn
            class="my-4 ai-outlined-btn"
            outlined
            @click="saveAndExitToDone"
            block
          >
            Save and Exit Agent
          </v-btn>
          <!-- PR-AGENT-ESCAPE: link to the main education editor for users
               who'd rather use the regular UI. -->
          <div class="text-center mt-3">
            <a
              href="#"
              class="text-caption text--secondary"
              @click.prevent="$router.push('/education')"
            >
              Switch to manual entry
            </a>
          </div>
        </div>
        <!-- PR-AGENT-UX: end-of-section "add more" screen. After the user
             reviews every parsed degree they land here — a clear list of
             every record on file plus a dedicated button to add degrees
             the parser missed. Keeps "review" and "add" as separate
             screens (Michael's feedback). -->
        <div v-if="addMode && !isEditing">
          <div class="pb-4 text-body-1">{{ addListIntro }}</div>
          <r-ai-bubble class="text-body-1 mt-2 mb-4">
            <div class="py-2 mb-1 font-weight-medium">
              Your Education ({{ educationCount }})
            </div>
            <div
              v-for="item in fullList"
              :key="item.key"
              class="ai-add-list-row py-2"
            >
              <div class="font-weight-medium">
                {{ rowTitle(item.general, "New degree — tap to fill in") }}
              </div>
              <div class="text-body-2 text--secondary">
                {{ rowTitle(item.degree, "Degree not yet entered") }}
              </div>
            </div>
            <div
              v-if="educationCount === 0"
              class="text-body-2 text--secondary py-2"
            >
              No education records yet.
            </div>
          </r-ai-bubble>
          <!-- PR-BUTTON-ORDER: Continue moved ABOVE Add so rapid-clickers
               from the previous review pages don't muscle-memory-click into
               a blank new entry. Add is still one-click reachable as the
               secondary outlined button below. -->
          <v-btn class="my-4" color="primary" @click="continueToWork" block>
            Continue to Work History
          </v-btn>
          <v-btn
            class="my-2 ai-outlined-btn"
            outlined
            @click="addAnotherDegree"
            block
          >
            <v-icon left>mdi-plus</v-icon>
            Add a Degree
          </v-btn>
          <v-btn
            class="my-2 ai-outlined-btn"
            outlined
            @click="saveAndExitToDone"
            block
          >
            Save and Exit Agent
          </v-btn>
          <!-- PR-AGENT-ESCAPE: link to the main education editor for users
               who'd rather use the regular UI. -->
          <div class="text-center mt-3">
            <a
              href="#"
              class="text-caption text--secondary"
              @click.prevent="$router.push('/education')"
            >
              Switch to manual entry
            </a>
          </div>
        </div>
      </v-col>
    </v-row>
  </r-ai-page-container>
</template>

<script>
import { mapFields } from "vuex-map-fields";
import { mapGetters, mapActions } from "vuex";
import * as builder from "../../store/modules/education_builder";
import AIMonthDateSelect from "../../components/AI/AIMonthDateSelect.vue";
import AILine from "../../components/AI/AILine.vue";
import * as disp from "@/utility/display.js";
import * as UUID from "../../utility/guid";
import { mapMutations } from "vuex";

export default {
  name: "AIEducation",
  data: () => ({
    isEditing: false,
    addMode: false,
    keyIndex: 0,
    noValues: false
  }),
  components: {
    "ai-month-date-select": AIMonthDateSelect,
    "ai-line": AILine
  },
  methods: {
    next() {
      if (this.keyIndex + 1 < this.fullList.length) {
        this.keyIndex += 1;
        this.key = this.fullList[this.keyIndex].key;
        this.isEditing = false;
      } else {
        // PR-AGENT-UX: reviewed every parsed degree — land on the
        // "add more" screen instead of jumping straight to work history.
        this.addMode = true;
        this.isEditing = false;
      }
    },
    continueToWork() {
      this.$router.push({ path: "/AIWork" });
    },
    rowTitle(value, fallback) {
      return value && String(value).trim() ? value : fallback;
    },
    edit() {
      this.isEditing = true;
    },
    async save() {
      await this.saveEducation();
      if (this.addMode) {
        // Saved a freshly-added degree — return to the add-more list.
        this.isEditing = false;
      } else {
        this.next();
      }
    },
    // PR-AGENT-UX: lets the user add a degree the parser missed (or that
    // they want to add fresh) without bailing out of the AI Upload flow.
    // Creates a new education entry in the store and switches the form
    // to edit-mode on the new entry.
    addAnotherDegree() {
      var newKey = UUID.gen();
      this.createEntry({ key: newKey });
      this.key = newKey;
      this.keyIndex = (this.fullList || []).findIndex(e => e.key === newKey);
      if (this.keyIndex < 0) this.keyIndex = (this.fullList || []).length - 1;
      this.addMode = true;
      this.isEditing = true;
    },
    // PR-AGENT-UX: same save-before-exit fix as AIWorkAchievements.
    async saveAndExitToDone() {
      try {
        await this.saveEducation();
      } catch (e) {
        console.warn("saveAndExitToDone failed:", e);
      }
      this.$router.push({ path: "/AIDone" });
    },
    ...mapMutations("education", ["createEntry"]),
    ...mapActions("education", ["saveEducation"])
  },
  computed: {
    ...mapGetters("education", ["fullList", "doesKeyExist", "baseDegrees"]),
    ...mapFields("education", {
      key: "key",
      eduDisplay: "educationGeneral.display",
      universityName: "educationGeneral.universityName",
      schoolName: "educationGeneral.schoolName",
      universityCity: "educationGeneral.universityCity",
      universityState: "educationGeneral.universityState",
      country: "educationGeneral.country",
      graduated: "educationGeneral.graduated",
      dateStart: "educationGeneral.dateStart",
      dateEnd: "educationGeneral.dateEnd",
      hasExpectedGrad: "educationGeneral.hasExpectedGrad",
      dateExpected: "educationGeneral.dateExpected",
      gpaDisplay: "educationGPA.display",
      gpa: "educationGPA.gpa",
      gpaScale: "educationGPA.gpaScale",
      degreeName: "educationDegree.degreeName",
      degreeArea: "educationDegree.degreeArea",
      degreeDisplay: "educationDegree.display",
      minors: "educationDegreeMinor.minors",
      minorDisplay: "educationDegreeMinor.display",
      educationGeneral: "educationGeneral",
      educationDegree: "educationDegree",
      educationDegreeMinor: "educationDegreeMinor",
      educationGPA: "educationGPA"
    }),
    educationCount() {
      return (this.fullList || []).length;
    },
    addListIntro() {
      return this.educationCount > 0
        ? "That's everything I have for your education. Add any degrees I missed, or continue to your work history."
        : "Add your education here, or continue to your work history.";
    },
    textIntro() {
      if (this.noValues) {
        return "We didn't find any education entries in your resume. You can add one in the full resumation tool.";
      } else if (this.keyIndex === 0) {
        return "Here's what I found for your first Education Record:";
      } else {
        return "Here's what I found for your next Education Record:";
      }
    },
    educationDisplayLine1() {
      if (this.noValues) {
        return "";
      }
      return builder.buildGeneralDisplay(this.educationGeneral);
    },
    educationDisplayLine2() {
      if (disp.IsNullorWhitespace(this.degreeName)) {
        return "We didn't find a degree field - please enter it.";
      }
      if (this.noValues) {
        return "";
      }
      return `${builder.buildDegree(
        this.educationDegree
      )} ${builder.buildMinorDegree(this.educationDegreeMinor)}`.trim();
    },
    degreeError() {
      return disp.IsNullorWhitespace(this.degreeName);
    },
    educationDisplayLine3() {
      if (this.noValues) {
        return "";
      }
      return builder.buildLocationDisplay(this.educationGeneral);
    },
    educationDisplayLine4() {
      if (this.noValues) {
        return "";
      }
      return builder.buildGPADisplay(this.educationGPA);
    }
  },
  beforeMount() {
    console.log("AIEducation beforeMount");
    if (this.fullList && this.fullList.length > 0) {
      // load first record
      this.key = this.fullList[0].key;
    } else {
      this.noValues = true;
    }
  }
};
</script>

<style scoped>
.ai-add-list-row {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.ai-add-list-row:last-child {
  border-bottom: none;
}
.theme--dark .ai-add-list-row {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}
</style>
